import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorState, LoadingState, EmptyState } from '../ui';
import {
    aggregateBranches, buildBranchRows, categoryBreakdown, priceAlignment, selectionTitle, sellSpeedBreakdown,
    verdictBreakdown,
} from '../../utils/branchView';
import {
    DEFAULT_COMPARE, addPools, benchCategoryShare, benchListing, benchSpeedShare, benchVerdictShare, benchmarkLabel,
    benchmarkLot, benchmarkName, benchmarkOf, benchmarkShort, buildCategoryCompare, buildCompareRows, compareOptions,
    normalizeBasis, normalizeChosen, chosenNamesTip, canonicalSearch, parseBranchesView, poolsByBranch, serializeBranchesView,
} from '../../utils/branchCompare';
import ComparePanel from './ComparePanel';
import SalesPerformance from './SalesPerformance';
import ScopeBar from './ScopeBar';
import SummaryBand, { ListingStrip } from './SummaryBand';
import ComparisonTable from './ComparisonTable';
import ProblemScatter from './ProblemScatter';
import { CategoryMixCard, AnomalySignalsCard, SellSpeedCard } from './DetailCards';
import AiValueTableV2 from './AiValueTableV2';
import { useDealershipSettings } from '../../contexts/settingsContext';

// "Chosen branches" with nothing picked: the prompt in place of the figures.
const PICK_PROMPT = 'Pick at least one branch to compare with.';

/**
 * The Branches tab: the scope bar (B1), the summary band (B2), the comparison
 * table that is also the branch picker (B3), the scatter (B4), the detail
 * cards and AI value by location. Selection is this tab's state: it starts
 * on the largest branch (D13). The selection, the compare basis and its
 * chosen branches are kept in the address (?branches=…&compare=…&vs=…,
 * docs/decisions/branch-compare.md),
 * so a reload, a shared link, a round trip through the other tabs and
 * Back / Forward restore them; no params is the default view.
 */
export default function BranchesOverviewV2({ data }) {
    const {
        combinedData, combinedLoading, combinedError, retryCombined, invUnits, invByBranch, inventoryLoading,
        inventoryLoaded, movementsData, movementsLoading, movementsError, retryMovements,
    } = data;
    const branches = useMemo(() => combinedData?.branches || [], [combinedData]);
    const allNames = useMemo(() => branches.map((b) => b.branch_name), [branches]);

    const location = useLocation();
    const navigate = useNavigate();
    const { criticalDays, gapThresholds, aging } = useDealershipSettings();
    const [selected, setSelected] = useState(() => new Set());
    const [standOutOnly, setStandOutOnly] = useState(false);
    const [compareRaw, setCompare] = useState(DEFAULT_COMPARE);
    // The "Chosen branches" benchmark (names) and its picker's open state.
    const [chosenRaw, setChosen] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const restored = useRef(false);
    // State, not the ref: the address sync waits for the render that has the
    // restored selection (the ref flips one render earlier).
    const [synced, setSynced] = useState(false);

    // The view an address names: its selection (else the largest branch) and basis.
    // The last address this tab wrote or read, so its own writes (which the
    // router reports a render later) are never mistaken for Back / Forward.
    const known = useRef(null);
    const applyAddress = (search) => {
        known.current = canonicalSearch(search);
        const view = parseBranchesView(search, branches.map((b) => b.branch_name));
        setCompare(view.compare);
        setChosen(view.chosen);
        setSelected(new Set(view.selected || [branches[0].branch_name]));
    };

    // D13: the largest branch (first in API order), once, when data arrives;
    // the first time, the address may name the selection and compare basis.
    useEffect(() => {
        if (!branches.length) return;
        if (!restored.current) {
            restored.current = true;
            setSynced(true);
            applyAddress(location.search);
            return;
        }
        // Functional: under StrictMode the effect runs twice on a remount with
        // the data already loaded, and the closure's `selected` is still the
        // empty first value, which would overwrite the restored selection.
        setSelected((prev) => (prev.size === 0 ? new Set([branches[0].branch_name]) : prev));
    }, [combinedData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Back / Forward (or any outside change of the address) while the tab is
    // open: follow it. Our own writes match the state, so they are no-ops.

    // In API order, so pills and links read the same way as the table.
    const selectedNames = useMemo(() => allNames.filter((n) => selected.has(n)), [allNames, selected]);
    const allSelected = allNames.length > 0 && selectedNames.length === allNames.length;

    const toggle = (name) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });
    const selectAll = () => setSelected(new Set(allNames));
    const clearAll = () => setSelected(new Set());
    const selectOnly = (name) => setSelected(new Set([name]));

    const compare = normalizeBasis(compareRaw, allNames.length);
    // Known names, page order, never a branch of the selection.
    const chosen = useMemo(() => normalizeChosen(chosenRaw, allNames, selectedNames), [chosenRaw, allNames, selectedNames]);
    // A chosen branch that joins the selection leaves the chosen set for good
    // (taking it out of the selection again does not bring it back).
    useEffect(() => {
        if (chosenRaw.some((nm) => selected.has(nm))) setChosen((prev) => prev.filter((nm) => !selected.has(nm)));
    }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
    const chosenKey = chosen.join('\n');
    useEffect(() => {
        if (compare !== 'chosen') setPickerOpen(false);
    }, [compare]);

    useEffect(() => {
        if (!synced || !branches.length) return;
        if (canonicalSearch(location.search) !== known.current) applyAddress(location.search);
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the address in step (replace: no history entry per click).
    useEffect(() => {
        if (!synced || !allNames.length) return;
        const search = serializeBranchesView(location.search, {
            selectedNames, allNames, compare, chosen,
        });
        known.current = search;
        if (search !== canonicalSearch(location.search)) {
            navigate(
                { pathname: location.pathname, search, hash: location.hash },
                { replace: true, state: location.state },
            );
        }
    }, [synced, selectedNames, compare, chosenKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const { rows, dealership } = useMemo(
        () => buildBranchRows(branches, invUnits, { gapThresholds }),
        [branches, invUnits, gapThresholds],
    );
    const aggregated = useMemo(() => aggregateBranches(branches, selected), [branches, selected]);
    const selectionUnits = useMemo(
        () => selectedNames.flatMap((n) => invByBranch[n] || []),
        [selectedNames, invByBranch],
    );
    const categories = useMemo(() => categoryBreakdown(selectionUnits, criticalDays), [selectionUnits, criticalDays]);
    const verdicts = useMemo(() => verdictBreakdown(selectionUnits), [selectionUnits]);
    const speed = useMemo(() => sellSpeedBreakdown(selectionUnits), [selectionUnits]);
    const alignment = useMemo(() => priceAlignment(selectionUnits, gapThresholds), [selectionUnits, gapThresholds]);

    // Compare mode: a partial selection against the chosen benchmark.
    const compareMode = selectedNames.length > 0 && !allSelected;
    const pools = useMemo(
        () => poolsByBranch(branches, invByBranch, { aging, gapThresholds }),
        [branches, invByBranch, aging, gapThresholds],
    );
    const comparison = useMemo(() => {
        if (!compareMode) return null;
        // "Chosen branches" with nothing picked: a prompt, no figures.
        if (compare === 'chosen' && !chosen.length) return { pick: true };
        const selPool = addPools(selectedNames.map((nm) => pools.byName.get(nm)).filter(Boolean));
        const bench = benchmarkOf(compare, pools, selected, chosen);
        const otherCount = allNames.length - selectedNames.length;
        const short = benchmarkShort(compare, chosen);
        const name = benchmarkName(compare, otherCount, chosen);
        // The names behind "chosen branches" on the cards (more than 3 picked).
        const title = compare === 'chosen' ? chosenNamesTip(chosen) : null;
        const rows = buildCompareRows(selPool, bench, { aging, gapThresholds }, pools.names.map((nm) => pools.byName.get(nm)));
        const benchOf = (id) => rows.find((r) => r.id === id)?.bench ?? null;
        // Whole dealership keeps today's band and AI value table (parity);
        // another basis replaces the band's "vs dealership" figures with the
        // panel's own benchmark and adds its row to AI value by location.
        const custom = compare !== DEFAULT_COMPARE;
        const lot = custom ? benchmarkLot(bench) : null;
        return {
            rows,
            categories: buildCategoryCompare(selPool, bench),
            label: benchmarkLabel(compare, otherCount, chosen),
            name,
            short,
            otherCount,
            cards: {
                category: { name: short, title, share: (cat) => benchCategoryShare(bench, cat) },
                verdict: { name: short, title, total: selPool.inv, share: (v) => benchVerdictShare(bench, v) },
                speed: { name: short, title, share: (k) => benchSpeedShare(bench, k) },
                listing: { name: short, title, ...benchListing(bench) },
            },
            band: custom ? {
                label: `vs ${short}`,
                aged: benchOf('aged'),
                risk: benchOf('risk'),
                gapName: compare === 'others' ? 'Other branches' : name,
                gapPct: lot.gapPct,
            } : null,
            // Branch names keep their case in the table's subtitle.
            aiValue: custom ? { label: name, summary: lot, phrase: compare === 'chosen' ? name : undefined } : null,
        };
    }, [compareMode, selectedNames, selected, pools, compare, chosen, allNames, aging, gapThresholds]);
    // The figures, unless the prompt stands in for them.
    const cmp = comparison && !comparison.pick ? comparison : null;

    if (combinedLoading) return <LoadingState label="Loading branch data..." />;
    if (combinedError) return <ErrorState message={combinedError} onRetry={retryCombined} />;
    if (!combinedData || !branches.length) return <EmptyState message="No branch data available." />;

    const linkBase = { names: selectedNames, allNames };
    const pickButton = <button type="button" className="v2-btn" onClick={() => setPickerOpen(true)}>Pick branches</button>;
    const inventoryReady = inventoryLoaded !== false && !(inventoryLoading && !invUnits?.length);
    const hasSelection = selectedNames.length > 0;

    return (
        <div className="br2-panel">
            <ScopeBar
                selectedNames={selectedNames}
                total={allNames.length}
                allSelected={allSelected}
                onRemove={toggle}
                onSelectAll={selectAll}
                onClear={clearAll}
                linkBase={linkBase}
                compare={compareMode ? {
                    value: compare,
                    options: compareOptions(allNames.length, selectedNames.length > 1),
                    onChange: setCompare,
                    chosen: compare === 'chosen' ? {
                        allNames, names: chosen, onChange: setChosen, open: pickerOpen, onOpenChange: setPickerOpen,
                    } : null,
                } : undefined}
            />

            {/* The branch picker comes first: it filters everything below it. */}
            <ComparisonTable
                rows={rows}
                dealership={dealership}
                selected={selected}
                allSelected={allSelected}
                onToggle={toggle}
                onSelectAll={selectAll}
                onClear={clearAll}
                onOnly={selectOnly}
                standOutOnly={standOutOnly}
                onToggleStandOut={() => setStandOutOnly((v) => !v)}
                allNames={allNames}
            />

            {hasSelection ? (
                <>
                    <SummaryBand
                        aggregated={aggregated}
                        dealership={dealership}
                        allSelected={allSelected}
                        selectionUnits={selectionUnits}
                        alignment={alignment}
                        linkBase={linkBase}
                        inventoryReady={inventoryLoaded !== false}
                        reference={cmp?.band}
                        noReference={Boolean(comparison?.pick)}
                    />
                    {comparison?.pick && (
                        <>
                            <section className="v2-card br2-noselect" aria-label="Chosen branches" data-testid="cmp-pick-prompt">
                                <EmptyState message={PICK_PROMPT} action={pickButton} />
                            </section>
                            {/* Sales performance keeps its place, with the same prompt (QA chosen #2). */}
                            <section className="v2-card br2-cmp br2-perf" aria-labelledby="br2-perf-h" data-testid="perf-pick-prompt">
                                <div className="br2-cmp-head">
                                    <h2 id="br2-perf-h">Sales performance</h2>
                                </div>
                                <div className="br2-perf-state">
                                    <EmptyState message={PICK_PROMPT} action={pickButton} />
                                </div>
                            </section>
                        </>
                    )}
                    {cmp && (
                        <ComparePanel
                            rows={cmp.rows}
                            categories={cmp.categories}
                            selectionName={selectionTitle(selectedNames, allNames.length)}
                            selectedNames={selectedNames}
                            benchName={cmp.name}
                            benchLabel={cmp.label}
                            benchShort={cmp.short}
                            basis={compare}
                            chosenNames={chosen}
                            otherCount={cmp.otherCount}
                            oldest={aging.boundaries[2]}
                            criticalDays={aging.criticalDays}
                            inventoryReady={inventoryReady}
                        />
                    )}
                    {cmp && (
                        <SalesPerformance
                            movements={movementsData}
                            loading={movementsLoading}
                            error={movementsError}
                            onRetry={retryMovements}
                            allNames={allNames}
                            selectedNames={selectedNames}
                            selectionName={selectionTitle(selectedNames, allNames.length)}
                            basis={compare}
                            chosenNames={chosen}
                            benchName={cmp.name}
                            benchShort={cmp.short}
                            otherCount={cmp.otherCount}
                            invByBranch={invByBranch}
                            inventoryReady={inventoryReady}
                            aging={aging}
                        />
                    )}
                    <ListingStrip aggregated={aggregated} reference={cmp?.cards.listing} />
                </>
            ) : (
                <section className="v2-card br2-noselect" aria-label="Summary">
                    <EmptyState
                        message="Tick branches in the comparison table above to see a summary."
                        action={<button type="button" className="v2-btn" onClick={selectAll}>Select all branches</button>}
                    />
                </section>
            )}

            <div className="br2-dgrid">
                <ProblemScatter
                    rows={rows}
                    dealership={dealership}
                    selected={selected}
                    onToggle={toggle}
                    standOutOnly={standOutOnly}
                />
                {hasSelection && (
                    <>
                        <CategoryMixCard categories={categories} reference={cmp?.cards.category} />
                        <div className="br2-stack">
                            <AnomalySignalsCard verdicts={verdicts} linkBase={linkBase} reference={cmp?.cards.verdict} />
                            <SellSpeedCard speed={speed} reference={cmp?.cards.speed} />
                        </div>
                    </>
                )}
            </div>

            {hasSelection && (
                <AiValueTableV2
                    units={invUnits}
                    loading={inventoryLoading}
                    selectedBranches={selected}
                    allSelected={allSelected}
                    selectionCount={selectedNames.length}
                    allNames={allNames}
                    benchmark={cmp?.aiValue}
                />
            )}
        </div>
    );
}

BranchesOverviewV2.propTypes = {
    data: PropTypes.object.isRequired,
};
